import { useState, Fragment, useEffect, useMemo } from 'react';
import { TextField, Autocomplete, Paper, IconButton, Divider, createFilterOptions, FormHelperText } from '@mui/material';
import styled from '@emotion/styled';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorMessage from '../MuiFields/ErrorMessage';
import { useField, useFormikContext } from 'formik';
import { get } from 'lodash';
import Dialog from '../utilities/Dialog';
import { useFormData } from '../../contexts/FormData';
import useInputDebounce from '../../hooks/useInputDebounce';

const CustomAutocomplete = styled(Autocomplete)``;

const filter = createFilterOptions();

const updateFields = (option, sfv, formUpdates) => {
  if (sfv && formUpdates?.length) {
    let updates = {};
    formUpdates.forEach(el => {
      updates[el.name] = option ? get(option, el.field) : '';
    });
    sfv(ov => ({ ...ov, ...updates }));
  }
}

const AutoCompleteSolo = ({
  fieldName: optionField, 
  popup = null, 
  newItemTitle = "Item", 
  displayLabel, 
  size = "small", 
  placeholder = "Search", 
  useForm = false, 
  FormComponent = null, 
  serverOptions, 
  forceServer = false, 
  displayField, 
  url, 
  style, 
  select, 
  formUpdates, 
  conditionStr, 
  server, 
  multiple = true, // Added default prop
  ...props
}) => {

  const { setFormValues } = useFormData() || {};
  let renderLabel = displayField || optionField;

  const { handleInputChange, loading, options, value: inputValue, setValue } = useInputDebounce({ 
    url, 
    state: true, 
    select, 
    serverOptions, 
    conditionStr, 
    forceServer 
  });

  const [field, meta, helpers] = useField(props);
  const { onBlur, value } = field;
  const [openDialog, setOpenDialog] = useState(false);

  // --- NEW: Map Formik IDs back to Option Objects for MUI display ---
  const muiValue = useMemo(() => {
    if (multiple) {
      if (!Array.isArray(value)) return [];
      return options.filter(opt => value.includes(opt[optionField]?.toString()));
    }
    return options.find(el => el[optionField]?.toString() === value?.toString()) || null;
  }, [value, options, multiple, optionField]);

  const handleChangeInput = (e, val, reason) => {
    if (["input", "clear"].includes(reason)) {
      setValue(val);
      if (handleInputChange) {
        handleInputChange(val, e);
      }
    }
  }

  useEffect(() => {
    // Only run sync logic for single select; Multi-select is handled by muiValue tags
    if (!multiple) {
      if (!value) {
        setValue('');
        updateFields(null, setFormValues, formUpdates);
      } else {
        let findOption = options?.find(el => el[optionField]?.toString() === value?.toString());
        if (!findOption && !FormComponent) return;
        
        setValue(findOption ? findOption[renderLabel] : '');
        updateFields(findOption, setFormValues, formUpdates);
      }
    }
  }, [value, options, multiple]);

  const handleChange = (e, newValue) => {
    if (multiple) {
      // Extract IDs from the array of selected options
      const selectedIds = (newValue || []).map(val => {
        if (typeof val === 'string') return val;
        return val[optionField]?.toString();
      });
      helpers.setValue(selectedIds);
    } else {
      // Existing single-select logic
      if (newValue) {
        if (typeof newValue === 'string') {
          helpers.setValue(newValue);
        } else {
          helpers.setValue(newValue[optionField]?.toString());
        }
      } else {
        helpers.setValue(null);
      }
    }
  };

  return (
    <Fragment>
      <CustomAutocomplete
        {...props}
        multiple={multiple}
        disableCloseOnSelect={true}
        loading={loading}
        popupIcon={popup}
        options={options}
        size={size}
        value={muiValue} // Pass the computed objects to MUI
        onInputChange={handleChangeInput} 
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option[renderLabel]?.toString() || option.inputValue || "";
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          let label = option.inputValue 
            ? option.title 
            : (displayLabel ? displayLabel(option) : option[renderLabel]);
          
          return (
            <li key={key} {...optionProps}>
              {label}
            </li>
          );
        }}
        onChange={(e, newValue) => {
          // Handle "Create New" selection
          const isCreatingNew = multiple 
            ? newValue.some(v => v.inputValue) 
            : (newValue && newValue.inputValue);

          if (isCreatingNew) {
            if (useForm || FormComponent) {
              setOpenDialog(true);
              // Clean up the search text without clearing existing selections
              if (!multiple) handleChange(e, "");
            } else {
              const newVal = multiple ? newValue[newValue.length - 1].inputValue : newValue.inputValue;
              handleChange(e, newVal);
            }
          } else {
            handleChange(e, newValue);
          }
        }}
        inputValue={inputValue}
        fullWidth
        freeSolo={false}
        clearOnBlur
        selectOnFocus
        onBlur={onBlur}
        filterOptions={(options, params) => {
          let filtered = server && !serverOptions?.length ? [...options] : filter(options, params);
          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              title: `Create New ${newItemTitle}: "${params.inputValue}"`,
            });
          }
          return filtered;
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            name={props.name} 
            placeholder={placeholder} 
            variant="outlined" 
            style={style} 
          />
        )}
      />
      {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
      
      <Dialog 
        size="md" 
        title={`Create New ${newItemTitle}`} 
        forceOpen={openDialog} 
        forceClose={() => setOpenDialog(false)} 
        text="" 
        saveText={`Save New ${newItemTitle}`} 
        useForm={true}
      >
        {FormComponent}
      </Dialog>
    </Fragment>
  )
};

export default AutoCompleteSolo;